/**
 * 郵便番号から住所を自動入力する機能
 * YubinBango ライブラリを使用
 */

declare global {
  interface Window {
    YubinBango?: any;
  }
}

export class PostalCodeAutoFill {
  private zipInput: HTMLInputElement | null;
  private prefSelect: HTMLSelectElement | null;
  private cityInput: HTMLInputElement | null;
  private streetInput: HTMLInputElement | null;
  private scriptLoaded: boolean = false;

  constructor() {
    console.log('[PostalCodeAutoFill] Initializing...');

    this.zipInput = document.getElementById('zip') as HTMLInputElement;
    this.prefSelect = document.getElementById('pref') as HTMLSelectElement;
    this.cityInput = document.getElementById('addr') as HTMLInputElement;
    this.streetInput = document.getElementById('addr--block') as HTMLInputElement;

    console.log('[PostalCodeAutoFill] Elements found:', {
      zipInput: this.zipInput,
      prefSelect: this.prefSelect,
      cityInput: this.cityInput,
      streetInput: this.streetInput
    });

    if (this.zipInput && this.prefSelect && this.cityInput) {
      console.log('[PostalCodeAutoFill] All elements found, calling init()');
      this.init();
    } else {
      console.error('[PostalCodeAutoFill] Required elements not found!');
    }
  }

  /**
   * 初期化処理
   */
  private async init(): Promise<void> {
    console.log('[PostalCodeAutoFill] init() called');

    // YubinBango ライブラリを読み込み
    try {
      await this.loadYubinBango();
      console.log('[PostalCodeAutoFill] YubinBango loaded successfully');
    } catch (error) {
      console.error('[PostalCodeAutoFill] Failed to load YubinBango:', error);
      return;
    }

    // 郵便番号入力フィールドにイベントリスナーを設定
    this.zipInput?.addEventListener('input', () => {
      console.log('[PostalCodeAutoFill] input event triggered');
      this.handleZipInput();
    });
    this.zipInput?.addEventListener('change', () => {
      console.log('[PostalCodeAutoFill] change event triggered');
      this.handleZipInput();
    });

    console.log('[PostalCodeAutoFill] Event listeners attached');
  }

  /**
   * YubinBango ライブラリを動的に読み込む
   */
  private loadYubinBango(): Promise<void> {
    return new Promise((resolve, reject) => {
      // すでに読み込まれている場合
      if (window.YubinBango || this.scriptLoaded) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://yubinbango.github.io/yubinbango/yubinbango.js';
      script.async = true;

      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };

      script.onerror = () => {
        console.error('YubinBango library failed to load');
        reject(new Error('Failed to load YubinBango'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * 郵便番号入力時の処理
   */
  private handleZipInput(): void {
    console.log('[PostalCodeAutoFill] handleZipInput() called');

    if (!this.zipInput) {
      console.error('[PostalCodeAutoFill] zipInput is null');
      return;
    }

    if (!window.YubinBango) {
      console.error('[PostalCodeAutoFill] YubinBango is not loaded');
      return;
    }

    const zipValue = this.zipInput.value.replace(/[^0-9]/g, ''); // 数字のみ抽出
    console.log('[PostalCodeAutoFill] Zip value:', zipValue, 'Length:', zipValue.length);

    // 郵便番号が7桁入力されたら住所を検索
    if (zipValue.length === 7) {
      console.log('[PostalCodeAutoFill] 7 digits entered, fetching address...');
      // ハイフンを追加してフォーマット（xxx-xxxx）
      const formattedZip = zipValue.slice(0, 3) + '-' + zipValue.slice(3);
      console.log('[PostalCodeAutoFill] Formatted zip:', formattedZip);

      // 入力フィールドもハイフン付きにフォーマット
      this.zipInput.value = formattedZip;

      this.fetchAddress(formattedZip);
    } else if (zipValue.length > 7) {
      // 8桁以上入力された場合は7桁に制限してハイフン付きにフォーマット
      const truncatedZip = zipValue.slice(0, 7);
      const formattedZip = truncatedZip.slice(0, 3) + '-' + truncatedZip.slice(3);
      this.zipInput.value = formattedZip;
    }
  }

  /**
   * 郵便番号から住所を取得
   */
  private fetchAddress(zipCode: string): void {
    console.log('[PostalCodeAutoFill] fetchAddress() called with:', zipCode);

    if (!window.YubinBango) {
      console.error('[PostalCodeAutoFill] YubinBango is not available');
      return;
    }

    // YubinBango の Core を使用して住所を取得
    new window.YubinBango.Core(zipCode, (address: any) => {
      console.log('[PostalCodeAutoFill] Address received:', address);

      // YubinBangoは l (locality) と m (town/street) というプロパティ名を使用
      // address.region = 都道府県
      // address.l = 市区町村
      // address.m = 町名
      if (address.region && address.l) {
        console.log('[PostalCodeAutoFill] Setting prefecture:', address.region);
        console.log('[PostalCodeAutoFill] Setting city:', address.l);
        console.log('[PostalCodeAutoFill] Setting street:', address.m || '');

        // 都道府県を設定
        this.setPrefecture(address.region);

        // 市区町村を設定（町名は含めない）
        if (this.cityInput) {
          this.cityInput.value = address.l;
        }

        // 町名を番地欄に設定
        if (this.streetInput && address.m) {
          this.streetInput.value = address.m;
        }
      } else {
        console.warn('[PostalCodeAutoFill] Address data incomplete:', address);
      }
    });
  }

  /**
   * 都道府県セレクトボックスの値を設定
   */
  private setPrefecture(prefectureName: string): void {
    console.log('[PostalCodeAutoFill] setPrefecture() called with:', prefectureName);

    if (!this.prefSelect) {
      console.error('[PostalCodeAutoFill] prefSelect is null');
      return;
    }

    // セレクトボックスのオプションから該当する都道府県を探す
    let found = false;
    for (let i = 0; i < this.prefSelect.options.length; i++) {
      const option = this.prefSelect.options[i];
      if (option.text === prefectureName || option.value === prefectureName) {
        console.log('[PostalCodeAutoFill] Match found at index:', i, 'Option:', option.text);
        this.prefSelect.selectedIndex = i;
        found = true;
        break;
      }
    }

    if (!found) {
      console.warn('[PostalCodeAutoFill] Prefecture not found in select options:', prefectureName);
      console.log('[PostalCodeAutoFill] Available options:',
        Array.from(this.prefSelect.options).map(o => o.text));
    }
  }
}
